/**
 * Tashi Design System Theme Constants
 * 
 * This file provides TypeScript constants for the Tashi color palette.
 * Use these constants in styled-components and inline styles instead of hardcoded hex values.
 * 
 * For CSS, use the CSS variables defined in tokens.css (e.g., var(--ds-primary))
 */

// Tashi Blue-Grey Scale (Primary Brand Color)
export const blueGrey = {
  1: '#e5e5eb',
  2: '#d2d2d9',
  3: '#b9bbc9',
  4: '#a1a4b9',
  5: '#8b8fa9',
  6: '#737795',
  7: '#606483',
  8: '#4a4e6a',
  9: '#363a50',
  10: '#212435',
} as const;

// Tashi Jade-Green Scale (Success)
export const jadeGreen = {
  1: '#f0faf7',
  2: '#d7f0e9',
  3: '#a2d4c5',
  4: '#5cb49b',
  5: '#11936f',
  6: '#128162',
  7: '#0e634b',
  8: '#0c4d3a',
  9: '#0a392c',
  10: '#04261d',
} as const;

// Tashi Garnet-Red Scale (Error/Danger)
export const garnetRed = {
  1: '#ffecec',
  2: '#ffc2c7',
  3: '#ff828a',
  4: '#ff4044',
  5: '#e51720',
  6: '#cc141d',
  7: '#a70000',
  8: '#850000',
  9: '#5f070b',
  10: '#430306',
} as const;

// Tashi Volcano Scale (Accent)
export const volcano = {
  1: '#ffeee9',
  2: '#facbbe',
  3: '#f6b19d',
  4: '#f3977d',
  5: '#ef7e5d',
  6: '#ea582c',
  7: '#e63812',
  8: '#d32b06',
  9: '#b62505',
  10: '#871c04',
} as const;

// Tashi Light-Blue Scale (Info)
export const lightBlue = {
  1: '#e3fbff',
  2: '#b9edf5',
  3: '#96e2ee',
  4: '#38bfd3',
  5: '#00a4ba',
  6: '#008b9e',
  7: '#0d7887',
  8: '#07627f',
  9: '#095661',
  10: '#03353d',
} as const;

// Tashi Golden-Topaz Scale (Warning)
export const goldenTopaz = {
  1: '#fff5e5',
  4: '#fcc46c',
  7: '#cb7e10',
  8: '#a8690f',
} as const;

// Tashi Neutral Scale
export const neutral = {
  1: '#fafafb',
  2: '#f5f5f5',
  3: '#f0f0f0',
  4: '#d9d9d9',
  5: '#bfbfbf',
  6: '#8c8c8c',
  7: '#6e6e6e',
  8: '#4d4d4d',
  9: '#262626',
} as const;

// Semantic Theme Colors
export const theme = {
  // Primary (Blue-Grey)
  primary: blueGrey[9],
  primaryHover: blueGrey[8],
  primaryLight: blueGrey[7],
  primaryForeground: '#ffffff',

  // Accent (Volcano)
  accent: volcano[6],
  accentHover: volcano[8],
  accentForeground: '#ffffff',

  // Surface
  surface: '#ffffff',
  surfaceMuted: neutral[1],
  surfaceStrong: blueGrey[1],

  // Border
  border: neutral[4],
  borderStrong: blueGrey[3],

  // Text
  textPrimary: blueGrey[10],
  textSecondary: blueGrey[6],
  textSubtle: blueGrey[5],

  // Status Colors
  success: jadeGreen[5],
  successLight: jadeGreen[1],
  warning: goldenTopaz[4],
  warningLight: goldenTopaz[1],
  danger: garnetRed[5],
  dangerHover: garnetRed[7],
  dangerLight: garnetRed[1],
  info: lightBlue[5],
  infoLight: lightBlue[1],

  // Gradients
  gradientPrimary: `linear-gradient(135deg, ${blueGrey[9]} 0%, ${blueGrey[7]} 100%)`,
  gradientAccent: `linear-gradient(135deg, ${volcano[5]} 0%, ${volcano[7]} 100%)`,

  // Shadows
  shadow: '0 10px 30px rgba(33, 36, 53, 0.08)',
  shadowHover: '0 4px 12px rgba(54, 58, 80, 0.4)',

  // Focus
  focusRing: '0 0 0 3px rgba(54, 58, 80, 0.1)',
} as const;

// Chart Colors (for recharts and other data visualization)
export const chartColors = [
  blueGrey[9],    // Primary
  jadeGreen[5],   // Success green
  goldenTopaz[4], // Warning yellow
  garnetRed[5],   // Error red
  volcano[5],     // Accent orange
  lightBlue[5],   // Info blue
  blueGrey[6],    // Secondary blue-grey
];

export default theme;
